import { baseApi } from "./baseApi";
import {
  CreateSubjectRequest,
  UpdateSubjectRequest,
  CopyCutSubjectRequest,
  SyncSubjectsRequest,
  SyncSubjectsPreview,
  SubjectDto,
  PurchaseableSubjectStudentDto,
} from "../../interfaces";
import { MediaDto } from "../../interfaces/media";
import { SelectListDto } from "../../models";

export const subjectApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSubject: builder.query<
      SubjectDto,
      string | { id: string; gradeId?: string }
    >({
      query: (arg) => {
        const id = typeof arg === "string" ? arg : arg.id;
        const gradeId = typeof arg === "string" ? undefined : arg.gradeId;
        return {
          url: gradeId
            ? `/api/subject/${id}?gradeId=${gradeId}`
            : `/api/subject/${id}`,
        };
      },
      providesTags: ["Subject"],
    }),
    getSubjectsByGrade: builder.query<SubjectDto[], string>({
      query: (gradeId) => ({
        url: `/api/subject/${gradeId}/grade`,
      }),
      providesTags: ["Subjects-By-Grade"],
    }),
    getSubjectsSelectListByGrade: builder.query<SelectListDto[], string>({
      query: (gradeId) => ({
        url: `/api/subject/select-list/${gradeId}/grade`,
      }),
      providesTags: ["Subject"],
    }),
    addSubject: builder.mutation<any, CreateSubjectRequest>({
      query: (data) => ({
        url: "/api/subject",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Subject", "Subjects-By-Grade"],
    }),
    updateSubject: builder.mutation<
      any,
      { id: string; data: UpdateSubjectRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/subject/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Subject", "Subjects-By-Grade"],
    }),
    deleteSubject: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/subject/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Subjects-By-Grade"],
    }),
    copyCutSubject: builder.mutation<any, CopyCutSubjectRequest>({
      query: (data) => ({
        url: "/api/subject/copy-cut",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Subjects-By-Grade"],
    }),
    previewSyncSubjects: builder.mutation<SyncSubjectsPreview, SyncSubjectsRequest>({
      query: (data) => ({
        url: "/api/subject/sync/preview",
        method: "POST",
        body: data,
      }),
    }),
    syncSubjects: builder.mutation<any, SyncSubjectsRequest>({
      query: (data) => ({
        url: "/api/subject/sync",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Subjects-By-Grade", "Subject"],
    }),
    toggleSubjectState: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/subject/toggle-state/${id}`,
        method: "PATCH",
      }),
      invalidatesTags: ["Subjects-By-Grade"],
    }),
    uploadSubjectHomeImage: builder.mutation<
      MediaDto,
      {
        file: File;
        subjectId?: string;
        onProgress?: (progress: number) => void;
      }
    >({
      query: ({ file, subjectId, onProgress }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: subjectId
            ? `/api/subject/upload/home-image?id=${subjectId}`
            : `/api/subject/upload/home-image`,
          method: "POST",
          data: formData,
          onUploadProgress: (event: any) => {
            if (event.total && onProgress) {
              onProgress(Math.round((event.loaded / event.total) * 100));
            }
          },
        };
      },
      invalidatesTags: ["Subject"],
    }),
    uploadSubjectContentImage: builder.mutation<
      MediaDto,
      {
        file: File;
        subjectId?: string;
        onProgress?: (progress: number) => void;
      }
    >({
      query: ({ file, subjectId, onProgress }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: subjectId
            ? `/api/subject/upload/content-image?id=${subjectId}`
            : `/api/subject/upload/content-image`,
          method: "POST",
          data: formData,
          onUploadProgress: (event: any) => {
            if (event.total && onProgress) {
              onProgress(Math.round((event.loaded / event.total) * 100));
            }
          },
        };
      },
      invalidatesTags: ["Subject"],
    }),
  }),
});

export const {
  useGetSubjectQuery,
  useGetSubjectsByGradeQuery,
  useGetSubjectsSelectListByGradeQuery,
  useAddSubjectMutation,
  useUpdateSubjectMutation,
  useDeleteSubjectMutation,
  useCopyCutSubjectMutation,
  usePreviewSyncSubjectsMutation,
  useSyncSubjectsMutation,
  useToggleSubjectStateMutation,
  useUploadSubjectHomeImageMutation,
  useUploadSubjectContentImageMutation,
} = subjectApi;
